import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { customerOriginIdSelector, meSelector, selectedSiteSelector, sitePrivilegesSelector, vpPrivilegesSelector } from '../../authentication/redux'
import { FeaturePrivileges } from './featurePrivileges'

export const useInterval = (callback: () => void, delay: number) => {
    const savedCallback = useRef<() => void>()

    useEffect(() => {
        savedCallback.current = callback
    })

    useEffect(() => {
        function tick() {
            savedCallback?.current && savedCallback.current()
        }

        if (delay !== null) {
            tick()
            let id = setInterval(tick, delay)
            return () => clearInterval(id)
        }
    }, [delay])
}

export const useUrlHelpers = () : {createUrl : ((postfix: string) => string), createEnterpriseUrl: ((postfix: string) => string)} => {
    const site = useSelector(selectedSiteSelector)
    const me = useSelector(meSelector)
    const customerOriginId = useSelector(customerOriginIdSelector)

    const createUrl = useCallback((postfix) => `/api/${site? `site/${site.productId}` : `enterprise/${me?.voiceCloudProductId}`}/${postfix}`, [site, me])

    return {
        createUrl: createUrl,
        createEnterpriseUrl: (postfix) => `/api/enterprise/${customerOriginId}/${postfix}`
    }
}

/**
 * Fine-grained privilege check hook.
 *
 * In site mode: checks sitePrivileges[currentSiteId] ∪ sitePrivileges["*"] for the privilege.
 * In enterprise mode: checks the flat vpPrivileges union.
 *
 * Returns false when the user has no VpUser record (not provisioned).
 *
 * @param privilege - a VpPrivilege name, e.g. 'SITE_DEVICES_WRITE'
 */
export const usePrivilege = (privilege: string): { hasAccess: boolean, onlyWithAccess: (element: ReactElement) => ReactElement } => {
    const vpPrivileges = useSelector(vpPrivilegesSelector)
    const sitePrivileges = useSelector(sitePrivilegesSelector)
    const site = useSelector(selectedSiteSelector)

    const hasAccess = useMemo(() => {
        if (vpPrivileges.length === 0) return false

        // _WRITE implies the corresponding _READ
        const privilegesToCheck = [privilege]
        if (privilege.endsWith('_READ')) {
            const writeVariant = privilege.replace(/_READ$/, '_WRITE')
            privilegesToCheck.push(writeVariant)
        }

        if (site) {
            const wildcardPrivs = sitePrivileges['*'] ?? []
            const sitePrivs = sitePrivileges[site.productId] ?? []
            return privilegesToCheck.some(p => wildcardPrivs.includes(p) || sitePrivs.includes(p))
        } else {
            return privilegesToCheck.some(p => vpPrivileges.includes(p))
        }
    }, [vpPrivileges, sitePrivileges, site, privilege])

    return {
        hasAccess,
        onlyWithAccess: (element: ReactElement) => hasAccess ? element : <></>,
    }
}

/**
 * Write-access check scoped to a specific user record.
 *
 * Returns true when:
 * - The current user has `SITE_USERS_WRITE` (full user-management write), OR
 * - The current user has `SITE_USERS_OWN_WRITE` AND the target user's email matches
 *   the logged-in user's email (editing one's own record).
 *
 * @param targetEmail - email of the user being edited; leave undefined to check full write only
 */
export const useUserWriteAccess = (targetEmail?: string): boolean => {
    const { hasAccess: hasUsersWrite } = usePrivilege(FeaturePrivileges.users.write)
    const { hasAccess: hasOwnWrite } = usePrivilege(FeaturePrivileges.usersOwn.write)
    const me = useSelector(meSelector)

    return useMemo(
        () => hasUsersWrite || (hasOwnWrite && !!targetEmail && targetEmail === me?.username),
        [hasUsersWrite, hasOwnWrite, targetEmail, me?.username],
    )
}

